import React, { useState } from 'react';
import { 
  ArrowLeft, User, MapPin, Phone, Mail, MessageSquare, Star, Clock, 
  Calendar, Euro, Settings, Eye, Edit, Plus, Trash2, Upload, Camera,
  CheckCircle, XCircle, AlertTriangle, Users, Timer, Award, CreditCard,
  FileText, Percent, Save, X, ChevronDown, ChevronUp, Home, Car, Plane,
  Shield, Baby, Utensils, Music, Heart, Briefcase, Gift, Zap, Globe,
  TrendingUp, DollarSign, BarChart3, PieChart, Activity, Download
} from 'lucide-react';

const OwnerDetail = ({ owner, onBack, onSave }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState({
    overview: false,
    properties: false,
    contracts: false,
    financial: false,
    communications: false,
    documents: false
  });

  const [editingOwner, setEditingOwner] = useState({
    // Informations générales
    id: owner?.id || 'OWN-2025-001',
    initials: owner?.initials || 'FB',
    name: owner?.name || 'Famille Beaumont',
    type: owner?.type || 'Exclusif PRIVIA',
    since: owner?.since || '2022',
    email: 'contact@beaumont-family.com',
    phone: '+33 6 12 34 56 78',
    address: {
      street: '123 Avenue des Champs-Élysées',
      city: 'Paris',
      postalCode: '75008',
      country: 'France'
    },
    
    // Portfolio
    propertiesCount: owner?.propertiesCount || 5,
    portfolioValue: owner?.portfolioValue || '€18.5M',
    properties: owner?.properties || ['Villa Azure Dreams', 'Villa Sunset Paradise'],
    locations: owner?.locations || ['St-Barthélemy', 'St-Tropez'],
    
    // Financier
    revenueYTD: owner?.revenueYTD || '€2.4M',
    commissionPrivia: owner?.commissionPrivia || '€312K',
    nextPayment: owner?.nextPayment || '15 Sept',
    amountDue: owner?.amountDue || '€245,000',
    occupancyRate: owner?.occupancyRate || 87,
    avgNightlyRate: '€2,850',
    totalBookings: 45,
    clientSatisfaction: 96,
    status: owner?.status || 'active',
    
    // Contrats
    contracts: [
      {
        id: 'CNT-2025-001',
        type: 'Contrat Exclusif PRIVIA',
        startDate: '2022-01-15',
        endDate: '2025-01-15',
        commissionRate: 13,
        longStayRate: 10,
        status: 'active',
        autoRenewal: true
      }
    ],
    
    // Communications
    communications: [
      {
        id: 'comm-001',
        date: '2024-09-10 14:30',
        type: 'email',
        direction: 'outbound',
        subject: 'Relevé mensuel - Août 2024',
        content: 'Veuillez trouver ci-joint le relevé détaillé...',
        attachments: ['Releve_Aout_2024.pdf'],
        status: 'sent'
      },
      {
        id: 'comm-002',
        date: '2024-09-05 16:20',
        type: 'phone',
        direction: 'inbound',
        subject: 'Demande d\'information tarification',
        content: 'Discussion sur les nouveaux tarifs saison haute',
        duration: '15 minutes',
        status: 'completed'
      }
    ],
    
    // Documents
    documents: [
      { id: 'doc-001', name: 'Contrat_Exclusif_2022.pdf', type: 'contract', uploadDate: '2022-01-15', size: '2.3 MB' },
      { id: 'doc-002', name: 'Assurance_Proprietes.pdf', type: 'insurance', uploadDate: '2024-01-10', size: '1.8 MB' },
      { id: 'doc-003', name: 'Factures_Maintenance_2024.pdf', type: 'financial', uploadDate: '2024-08-30', size: '956 KB' }
    ]
  });

  const handleToggleEdit = (section) => {
    setEditMode({ ...editMode, [section]: !editMode[section] });
  };

  const handleSave = (section) => {
    onSave(editingOwner);
    setEditMode({ ...editMode, [section]: false });
  };

  const handleCancel = (section) => {
    setEditingOwner({ ...editingOwner });
    setEditMode({ ...editMode, [section]: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="owner-detail min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={20} />
              <span>Retour aux propriétaires</span>
            </button>
            <div className="border-l border-gray-300 pl-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold">
                  {editingOwner.initials}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{editingOwner.name}</h1>
                  <p className="text-gray-600">{editingOwner.type} • Membre depuis {editingOwner.since}</p>
                </div>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(editingOwner.status)}`}>
                  {editingOwner.status === 'active' ? 'Actif' : editingOwner.status}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <CreditCard size={18} />
              <span>Paiement ({editingOwner.amountDue})</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <BarChart3 size={18} />
              <span>Rapport</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              <Mail size={18} />
              <span>Contact</span>
            </button>
          </div>
        </div>

        {/* KPIs de résumé */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Home className="text-blue-600" size={20} />
              <span className="text-xs text-blue-600">Portfolio</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{editingOwner.propertiesCount}</div>
            <div className="text-sm text-gray-600">Propriétés</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-green-600" size={20} />
              <span className="text-xs text-green-600">Valeur</span>
            </div>
            <div className="text-xl font-bold text-green-600">{editingOwner.portfolioValue}</div>
            <div className="text-sm text-gray-600">Portfolio</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-yellow-600" size={20} />
              <span className="text-xs text-yellow-600">Revenue</span>
            </div>
            <div className="text-lg font-bold text-yellow-600">{editingOwner.revenueYTD}</div>
            <div className="text-sm text-gray-600">YTD 2024</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <PieChart className="text-purple-600" size={20} />
              <span className="text-xs text-purple-600">Occupation</span>
            </div>
            <div className="text-lg font-bold text-purple-600">{editingOwner.occupancyRate}%</div>
            <div className="text-sm text-gray-600">Taux moyen</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-red-600" size={20} />
              <span className="text-xs text-red-600">Satisfaction</span>
            </div>
            <div className="text-lg font-bold text-red-600">{editingOwner.clientSatisfaction}%</div>
            <div className="text-sm text-gray-600">Note clients</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Onglets */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: Eye },
                { id: 'properties', label: 'Propriétés', icon: Home },
                { id: 'contracts', label: 'Contrats', icon: FileText },
                { id: 'financial', label: 'Financier', icon: CreditCard },
                { id: 'communications', label: 'Communications', icon: MessageSquare },
                { id: 'documents', label: 'Documents', icon: Upload }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Onglet Vue d'ensemble */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Vue d'ensemble du propriétaire</h3>
                    <p className="text-gray-600 mt-1">Informations principales et performance</p>
                  </div>
                  {!editMode.overview && (
                    <button
                      onClick={() => handleToggleEdit('overview')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white rounded-xl hover:from-yellow-700 hover:to-yellow-800 shadow-lg transition-all duration-200"
                    >
                      <Edit size={18} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Informations Contact */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <User className="text-white" size={20} />
                        </div>
                        Informations Contact
                      </h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nom complet</label>
                        {editMode.overview ? (
                          <input
                            type="text"
                            value={editingOwner.name}
                            onChange={(e) => setEditingOwner({...editingOwner, name: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        ) : (
                          <div className="bg-blue-50 px-4 py-3 rounded-xl border border-blue-200">
                            <span className="font-semibold text-blue-800">{editingOwner.name}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                        {editMode.overview ? (
                          <input
                            type="email"
                            value={editingOwner.email}
                            onChange={(e) => setEditingOwner({...editingOwner, email: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        ) : (
                          <div className="flex items-center space-x-2 bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                            <Mail className="text-gray-600" size={16} />
                            <span className="font-medium text-gray-800">{editingOwner.email}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
                        {editMode.overview ? (
                          <input
                            type="tel"
                            value={editingOwner.phone}
                            onChange={(e) => setEditingOwner({...editingOwner, phone: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        ) : (
                          <div className="flex items-center space-x-2 bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                            <Phone className="text-gray-600" size={16} />
                            <span className="font-medium text-gray-800">{editingOwner.phone}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
                        <div className="bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                          <div className="flex items-start space-x-2">
                            <MapPin className="text-gray-600 mt-1" size={16} />
                            <div className="text-sm text-gray-800">
                              <div>{editingOwner.address.street}</div>
                              <div>{editingOwner.address.postalCode} {editingOwner.address.city}</div>
                              <div>{editingOwner.address.country}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Performance et Métriques */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <BarChart3 className="text-white" size={20} />
                        </div>
                        Performance & Métriques
                      </h4>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600 mb-2">{editingOwner.avgNightlyRate}</div>
                            <div className="text-sm font-medium text-green-700">Prix nuit moyen</div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600 mb-2">{editingOwner.totalBookings}</div>
                            <div className="text-sm font-medium text-blue-700">Réservations YTD</div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-xl border border-yellow-200">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-600 mb-2">{editingOwner.occupancyRate}%</div>
                            <div className="text-sm font-medium text-yellow-700">Taux occupation</div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600 mb-2">{editingOwner.clientSatisfaction}%</div>
                            <div className="text-sm font-medium text-purple-700">Satisfaction client</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {editMode.overview && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('overview')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('overview')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Onglet Propriétés */}
            {activeTab === 'properties' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Portfolio de Propriétés</h3>
                    <p className="text-gray-600 mt-1">Gestion et suivi des propriétés du propriétaire</p>
                  </div>
                  <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200">
                    <Plus size={18} />
                    <span>Ajouter Propriété</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {editingOwner.properties.map((property, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-gray-800">{property}</h4>
                          <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                            Active
                          </span>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <MapPin className="text-gray-500" size={16} />
                            <span className="text-gray-700">{editingOwner.locations[index]}</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Type:</span>
                              <span className="font-medium text-gray-800 ml-2">Villa Luxe</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Chambres:</span>
                              <span className="font-medium text-gray-800 ml-2">6</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Capacité:</span>
                              <span className="font-medium text-gray-800 ml-2">12 personnes</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Prix/nuit:</span>
                              <span className="font-medium text-green-600 ml-2">€2,850</span>
                            </div>
                          </div>

                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <div className="text-lg font-bold text-blue-600">89%</div>
                                <div className="text-xs text-gray-600">Occupation</div>
                              </div>
                              <div>
                                <div className="text-lg font-bold text-green-600">€285K</div>
                                <div className="text-xs text-gray-600">Revenue YTD</div>
                              </div>
                              <div>
                                <div className="text-lg font-bold text-yellow-600">4.9</div>
                                <div className="text-xs text-gray-600">Note clients</div>
                              </div>
                            </div>
                          </div>

                          <div className="flex space-x-2">
                            <button className="flex-1 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                              <Eye size={14} className="inline mr-1" />
                              Voir détails
                            </button>
                            <button className="flex-1 px-3 py-2 text-sm text-green-600 border border-green-300 rounded-lg hover:bg-green-50">
                              <Calendar size={14} className="inline mr-1" />
                              Planning
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Autres onglets - Placeholders pour l'instant */}
            {activeTab === 'contracts' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Contrats</h3>
                    <p className="text-gray-600 mt-1">Gestion des contrats et accords</p>
                  </div>
                  <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 shadow-lg transition-all duration-200">
                    <Plus size={18} />
                    <span>Nouveau Contrat</span>
                  </button>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  {editingOwner.contracts.map((contract, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-800">{contract.type}</h4>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(contract.status)}`}>
                          {contract.status === 'active' ? 'Actif' : contract.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Début:</span>
                          <div className="font-medium text-gray-800">{new Date(contract.startDate).toLocaleDateString('fr-FR')}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Fin:</span>
                          <div className="font-medium text-gray-800">{new Date(contract.endDate).toLocaleDateString('fr-FR')}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Commission:</span>
                          <div className="font-medium text-green-600">{contract.commissionRate}%</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Long séjour:</span>
                          <div className="font-medium text-blue-600">{contract.longStayRate}%</div>
                        </div>
                      </div>

                      <div className="mt-4 flex space-x-2">
                        <button className="px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                          <FileText size={14} className="inline mr-1" />
                          Voir contrat
                        </button>
                        {contract.autoRenewal && (
                          <span className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg">
                            <CheckCircle size={14} className="inline mr-1" />
                            Renouvellement automatique
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'financial' && (
              <div className="text-center py-12">
                <CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">Onglet Financier</h3>
                <p className="text-gray-600">Informations financières détaillées (à implémenter)</p>
              </div>
            )}

            {activeTab === 'communications' && (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">Onglet Communications</h3>
                <p className="text-gray-600">Historique des communications (à implémenter)</p>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="text-center py-12">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">Onglet Documents</h3>
                <p className="text-gray-600">Gestion des documents (à implémenter)</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerDetail;